// ---------------------------------------------------------------------------
// Sprint 8 A3: Health Score Visualization
// ---------------------------------------------------------------------------
// Gauge 0-100, radar chart (5 dimensions), 30-day trend line.
// Uses healthScoreHistory table data.

import { useState } from "react";

interface HealthScoreData {
  compositeScore: number; // 0-100
  dimensionScores: Record<string, number>; // e.g. { engagement: 80, revenue: 60, ... }
  computedAt: string;
}

interface HealthScorePanelProps {
  current: HealthScoreData | null;
  history: HealthScoreData[]; // last 30 days, sorted by computedAt ascending
  thresholds?: { green: number; yellow: number }; // defaults: green=70, yellow=40
}

const DEFAULT_THRESHOLDS = { green: 70, yellow: 40 };

const DIMENSION_LABELS: Record<string, string> = {
  engagement: "Engagement",
  revenue: "Revenue",
  satisfaction: "Satisfaction",
  service: "Service",
  communication: "Communication",
};

function getScoreColor(score: number, thresholds = DEFAULT_THRESHOLDS): string {
  if (score >= thresholds.green) return "text-[var(--color-success)]";
  if (score >= thresholds.yellow) return "text-[var(--color-warning)]";
  return "text-[var(--color-destructive)]";
}

function getScoreBg(score: number, thresholds = DEFAULT_THRESHOLDS): string {
  if (score >= thresholds.green) return "bg-[var(--color-success)]/50";
  if (score >= thresholds.yellow) return "bg-[var(--color-warning)]/50";
  return "bg-[var(--color-destructive)]/50";
}

export function HealthScorePanel({ current, history, thresholds = DEFAULT_THRESHOLDS }: HealthScorePanelProps) {
  const [activeTab, setActiveTab] = useState<"gauge" | "radar" | "trend">("gauge");

  if (!current) {
    return (
      <div className="text-sm text-[var(--color-muted-foreground)] py-4 text-center border border-dashed border-[var(--color-border)] rounded-lg">
        No health score computed yet
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Tab switcher */}
      <div className="flex items-center gap-1 border-b border-[var(--color-border)]">
        {(["gauge", "radar", "trend"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 text-xs capitalize ${
              activeTab === tab
                ? "border-b-2 border-[var(--color-primary)]/80 text-[var(--color-primary)] font-medium"
                : "text-[var(--color-muted-foreground)] hover:text-[var(--color-muted-foreground)]"
            }`}
          >
            {tab === "gauge" ? "Score" : tab === "radar" ? "Dimensions" : "Trend"}
          </button>
        ))}
      </div>

      {activeTab === "gauge" && (
        <ScoreGauge score={current.compositeScore} thresholds={thresholds} />
      )}

      {activeTab === "radar" && (
        <DimensionRadar dimensions={current.dimensionScores} />
      )}

      {activeTab === "trend" && (
        <TrendLine history={history} thresholds={thresholds} />
      )}
    </div>
  );
}

/**
 * Visual gauge 0-100 with color zones.
 */
function ScoreGauge({ score, thresholds }: { score: number; thresholds: { green: number; yellow: number } }) {
  const colorClass = getScoreColor(score, thresholds);
  const bgClass = getScoreBg(score, thresholds);
  const rotation = (score / 100) * 180 - 90; // -90 to 90 degrees

  return (
    <div className="flex flex-col items-center py-4">
      {/* Semi-circular gauge */}
      <div className="relative w-32 h-16 overflow-hidden">
        <div className="absolute inset-0 rounded-t-full border-8 border-[var(--color-border)]" />
        <div
          className={`absolute bottom-0 left-1/2 w-1 h-14 origin-bottom rounded-full ${bgClass}`}
          style={{ transform: `translateX(-50%) rotate(${rotation}deg)` }}
        />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[var(--color-background)] border-2 border-[var(--color-border)]" />
      </div>
      <div className={`text-3xl font-bold mt-2 ${colorClass}`}>{score}</div>
      <div className="text-xs text-[var(--color-muted-foreground)]">out of 100</div>
      {/* Zone labels */}
      <div className="flex gap-4 mt-2 text-[10px] text-[var(--color-muted-foreground)]">
        <span>🔴 &lt;{thresholds.yellow}</span>
        <span>🟡 {thresholds.yellow}-{thresholds.green}</span>
        <span>🟢 &gt;{thresholds.green}</span>
      </div>
    </div>
  );
}

/**
 * Radar chart showing 5 health dimensions.
 * Pure CSS/HTML implementation — no chart library dependency.
 */
function DimensionRadar({ dimensions }: { dimensions: Record<string, number> }) {
  const entries = Object.entries(dimensions);

  return (
    <div className="space-y-2 py-2">
      {entries.map(([key, value]) => {
        const label = DIMENSION_LABELS[key] ?? key;
        const percentage = Math.min(value, 100);
        const color = value >= 70 ? "bg-[var(--color-success)]/40" : value >= 40 ? "bg-[var(--color-warning)]/40" : "bg-[var(--color-destructive)]/40";

        return (
          <div key={key} className="flex items-center gap-2">
            <span className="text-xs text-[var(--color-muted-foreground)] w-28 text-right">{label}</span>
            <div className="flex-1 h-2 bg-[var(--color-muted)] rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${color}`}
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className="text-xs text-[var(--color-muted-foreground)] w-8 text-right">{value}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 30-day trend line using CSS-based sparkline.
 */
function TrendLine({ history, thresholds }: { history: HealthScoreData[]; thresholds: { green: number; yellow: number } }) {
  if (history.length === 0) {
    return <div className="text-xs text-[var(--color-muted-foreground)] py-4 text-center">No trend data available</div>;
  }

  const scores = history.map((h) => h.compositeScore);
  const maxScore = 100;
  const latest = scores[scores.length - 1] ?? 0;
  const earliest = scores[0] ?? 0;
  const delta = latest - earliest;
  const firstEntry = history[0];
  const lastEntry = history[history.length - 1];

  return (
    <div className="py-2">
      {/* Sparkline */}
      <div className="flex items-end gap-px h-16">
        {scores.map((score, i) => {
          const height = (score / maxScore) * 100;
          const color = getScoreBg(score, thresholds);
          const entry = history[i];
          return (
            <div
              key={i}
              className={`flex-1 rounded-t-sm ${color} opacity-80`}
              style={{ height: `${height}%` }}
              title={entry ? `${entry.computedAt.slice(0, 10)}: ${score}` : String(score)}
            />
          );
        })}
      </div>
      {/* Summary */}
      <div className="flex justify-between mt-2 text-xs text-[var(--color-muted-foreground)]">
        <span>{firstEntry?.computedAt?.slice(0, 10) ?? ""}</span>
        <span className={delta >= 0 ? "text-[var(--color-success)]" : "text-[var(--color-destructive)]"}>
          {delta >= 0 ? "+" : ""}{delta} pts
        </span>
        <span>{lastEntry?.computedAt?.slice(0, 10) ?? ""}</span>
      </div>
    </div>
  );
}
